import { GUIDE_DOTS } from '@/app/guide/guideDot';
import AppIcon from '@/components/AppIcon';
import { StatusDot } from '@/components/StatusDot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { spaceRank } from '@/lib/spaceOrder';
import { EquipmentTypeDisplay } from '@/nodes/NodeUI';
import { SpaceFilterPills, useSpaceFilter, useSpaceFilteredRows } from '@/app/equipmentTypes/SpaceFilterPills';
import { allEquipmentTypesState, equipmentTypeDraftStatusState, flowsByTypeIdState, spacesState } from '@/state';
import { guideHealthState } from '@/state/guide.state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { EquipmentTypeDao } from '@rock/db-admin';
import type { EquipmentType, EquipmentTypeId, Space, SpaceId } from '@rock/types';
import { groupBy, orderBy } from 'lodash';
import { Edit, GripVertical } from 'lucide-react';
import { type DragEvent, type FC, useMemo, useState } from 'react';
import { toast } from 'sonner';

type DropTarget = { id: EquipmentTypeId; position: 'above' | 'below' };

const log = logger('CatalogTab');
const equipmentTypeDao = new EquipmentTypeDao();

/** The equipment-type catalog, live types overlaid with their drafts, under the space filter. */
export const CatalogTab: FC<{
    types: EquipmentType[];
    loaded: boolean;
    onEdit: (typeId: EquipmentTypeId) => void;
}> = ({ types, loaded, onEdit }) => {
    const spaces = useAsyncAtomValue(spacesState, []);
    const spaceFilter = useSpaceFilter();
    const visibleTypes = useSpaceFilteredRows(types);

    return (
        <>
            <SpaceFilterPills rows={types} />
            {loaded ? (
                <CatalogGroups types={visibleTypes} spaces={spaces} spaceFilter={spaceFilter} onEdit={onEdit} />
            ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            )}
        </>
    );
};

/**
 * Rows group by the type's own `group` once a space is picked, and by space when the filter is "All" —
 * a group name only means something inside its space, so across spaces the space IS the grouping.
 *
 * Reordering follows the same line: `order` is a rank within one space's group, so dragging is
 * offered only in a single-space view.
 */
const CatalogGroups: FC<{
    types: EquipmentType[];
    spaces: Space[];
    spaceFilter: SpaceId | undefined;
    onEdit: (typeId: EquipmentTypeId) => void;
}> = ({ types, spaces, spaceFilter, onEdit }) => {
    if (types.length === 0) {
        return (
            <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                No equipment types here yet.
            </div>
        );
    }

    if (spaceFilter) {
        const byGroup = groupBy(types, 'group');
        return (
            <div>
                {Object.keys(byGroup)
                    .sort((a, b) => a.localeCompare(b))
                    .map((group) => (
                        <TypeGroup
                            key={group}
                            title={group}
                            types={byGroup[group] ?? []}
                            spaceId={spaceFilter}
                            reorderable
                            allTypesInSpace={types}
                            onEdit={onEdit}
                        />
                    ))}
            </div>
        );
    }

    const bySpace = groupBy(types, 'spaceId');
    const spaceNames = new Map(spaces.map((space) => [space.id, space.name]));
    return (
        <div>
            {orderBy(Object.keys(bySpace), (spaceId) => spaceRank(spaceId)).map((spaceId) => (
                <TypeGroup
                    key={spaceId}
                    title={spaceNames.get(spaceId as SpaceId) ?? spaceId}
                    types={bySpace[spaceId] ?? []}
                    spaceId={spaceId as SpaceId}
                    reorderable={false}
                    allTypesInSpace={bySpace[spaceId] ?? []}
                    onEdit={onEdit}
                />
            ))}
        </div>
    );
};

const TypeGroup: FC<{
    title: string;
    types: EquipmentType[];
    spaceId: SpaceId;
    /** Drag-to-reorder is only meaningful within one space's groups — see `CatalogGroups`. */
    reorderable: boolean;
    /** Every type in this group's space — reordering has to renumber the group it drops INTO. */
    allTypesInSpace: EquipmentType[];
    onEdit: (typeId: EquipmentTypeId) => void;
}> = ({ title, types, spaceId, reorderable, allTypesInSpace, onEdit }) => {
    const onRowClick = useRowClick();
    const flowsByTypeId = useAsyncAtomValue(flowsByTypeIdState(spaceId), new Map<EquipmentTypeId, string>());
    // Reorder persists `order` to the *live* doc, which only exists for published types — so it operates
    // on live types only. Draft-only (never-published) types can't be reordered until they're published.
    const liveTypes = useAsyncAtomValue(allEquipmentTypesState, []);

    const [draggedId, setDraggedId] = useState<EquipmentTypeId | null>(null);
    const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

    const resetDrag = () => {
        setDraggedId(null);
        setDropTarget(null);
    };

    const handleDragOver = (e: DragEvent<HTMLTableRowElement>, targetId: EquipmentTypeId) => {
        if (!draggedId || draggedId === targetId) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const position: DropTarget['position'] = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
        setDropTarget((prev) =>
            prev && prev.id === targetId && prev.position === position ? prev : { id: targetId, position },
        );
    };

    const handleDrop = async () => {
        const dragged = allTypesInSpace.find((t) => t.id === draggedId);
        const target = allTypesInSpace.find((t) => t.id === dropTarget?.id);
        const position = dropTarget?.position;
        resetDrag();
        if (!dragged || !target || !position || dragged.id === target.id) return;
        const liveIds = new Set(liveTypes.filter((t) => t.spaceId === spaceId).map((t) => t.id));
        if (!liveIds.has(dragged.id)) {
            toast.error('Publish this type before reordering it');
            return;
        }
        await reorderEquipmentTypes(spaceId, allTypesInSpace, dragged, target, position, liveIds);
    };

    return (
        <div className="py-5">
            <h4 className="mb-2 text-lg font-semibold">{title}</h4>
            <Table className="not-prose">
                <TableHeader>
                    <TableRow>
                        {/* `order` only ranks a type within its own group, so across spaces the group
                            it belongs to is the useful column and the bare number is noise. */}
                        <TableHead>{reorderable ? '#' : 'Group'}</TableHead>
                        <TableHead>ID</TableHead>
                        <TableHead>Type</TableHead>
                        {/* Subtitle rides under the title rather than in a column of its own — it's
                            the same thought, and the table is wide enough already. */}
                        <TableHead>Title</TableHead>
                        <TableHead>Guide</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {orderBy(types, reorderable ? ['order'] : ['group', 'order']).map((type) => {
                        const isDropTarget = dropTarget?.id === type.id && draggedId && draggedId !== type.id;
                        const dropIndicatorClass = isDropTarget
                            ? dropTarget.position === 'above'
                                ? 'border-t-2 border-t-primary'
                                : 'border-b-2 border-b-primary'
                            : '';
                        return (
                            <TableRow
                                key={type.id}
                                draggable={reorderable}
                                className={`group/row hover:bg-muted cursor-pointer ${dropIndicatorClass} ${draggedId === type.id ? 'opacity-50' : ''}`}
                                onClick={onRowClick(`/equipment/types/${type.id}`)}
                                {...(reorderable && {
                                    onDragStart: (e: DragEvent<HTMLTableRowElement>) => {
                                        setDraggedId(type.id);
                                        e.dataTransfer.effectAllowed = 'move';
                                    },
                                    onDragOver: (e: DragEvent<HTMLTableRowElement>) => handleDragOver(e, type.id),
                                    onDrop: handleDrop,
                                    onDragEnd: resetDrag,
                                })}
                            >
                                <TableCell>
                                    {reorderable ? (
                                        <div className="flex flex-row items-center gap-1">
                                            <GripVertical
                                                className="size-4 text-muted-foreground opacity-0 max-md:opacity-100 group-hover/row:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                                aria-label="Drag to reorder"
                                            />
                                            {type.order}
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground">{type.group}</span>
                                    )}
                                </TableCell>
                                <TableCell className="flex flex-row gap-2 items-center">
                                    {type.icon && <AppIcon icon={type.icon} />}{' '}
                                    <EquipmentTypeDisplay spaceId={type.spaceId} typeId={type.id} />
                                </TableCell>
                                <TableCell>
                                    <div>
                                        {type.scope}
                                        {type.specs?.length ? ` (${type.specs.length} specs)` : ''}
                                    </div>
                                    {type.scope === 'system' && (
                                        <div className="flex flex-col items-center p-2 gap-1 text-xs text-gray-500">
                                            System Parts:
                                            <div className="flex flex-row flex-wrap gap-1">
                                                {type.systemParts?.map((p) => (
                                                    <EquipmentTypeDisplay
                                                        spaceId={type.spaceId}
                                                        key={p.typeId}
                                                        typeId={p.typeId}
                                                    />
                                                ))}
                                                {!type.systemParts?.length ? 'None' : ''}
                                            </div>
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-row items-center gap-2">
                                        {type.title}
                                        <RowStageBadge typeId={type.id} />
                                    </div>
                                    {type.subtitle && (
                                        <div className="text-xs text-muted-foreground">{type.subtitle}</div>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <RowGuide spaceId={spaceId} typeId={type.id} name={flowsByTypeId.get(type.id)} />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="opacity-0 max-md:opacity-100 group-hover/row:opacity-100 transition-opacity cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(type.id);
                                        }}
                                    >
                                        <Edit />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

/**
 * A type's guide, led by its status dot — dot first so the dots line up down the column even where
 * the names don't. Rendered only when there IS a guide, which is also what keeps the health read
 * (one document per row) off the many types that have none.
 */
export const RowGuide: FC<{ spaceId: SpaceId; typeId: EquipmentTypeId; name: string | undefined }> = ({
    spaceId,
    typeId,
    name,
}) => {
    if (!name) return <span className="text-muted-foreground">-</span>;
    return <NamedGuide spaceId={spaceId} typeId={typeId} name={name} />;
};

const NamedGuide: FC<{ spaceId: SpaceId; typeId: EquipmentTypeId; name: string }> = ({ spaceId, typeId, name }) => {
    // Stable object — the flow atom families key on it, so a fresh literal each render thrashes them.
    const params = useMemo(() => ({ spaceId, equipmentTypeId: typeId }), [spaceId, typeId]);
    const health = useAsyncAtomValue(guideHealthState(params), 'none');
    return (
        <div className="flex flex-row items-center gap-2">
            <StatusDot dot={GUIDE_DOTS[health]} />
            {name}
        </div>
    );
};

// Per-row indicator that a type has unpublished draft changes (or is a never-published draft).
const RowStageBadge: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const status = useAsyncAtomValue(equipmentTypeDraftStatusState(typeId), undefined);
    if (status === 'draft-differs' || status === 'draft-pending') {
        return (
            <Badge variant="outline" className="text-blue-700 border-blue-700 bg-background">
                Draft
            </Badge>
        );
    }
    return null;
};

async function reorderEquipmentTypes(
    spaceId: SpaceId,
    types: EquipmentType[],
    dragged: EquipmentType,
    target: EquipmentType,
    position: DropTarget['position'],
    liveIds: Set<EquipmentTypeId>,
) {
    const sourceGroup = dragged.group;
    const destGroup = target.group;

    // Only renumber live types — `order` writes go to the live doc (drafts have no order of their own).
    const destList = orderBy(
        types.filter((t) => t.group === destGroup && t.id !== dragged.id && liveIds.has(t.id)),
        'order',
    );
    const targetIndex = destList.findIndex((t) => t.id === target.id);
    const insertIndex = targetIndex + (position === 'below' ? 1 : 0);
    destList.splice(insertIndex, 0, dragged);

    const updates: Promise<void>[] = [];
    destList.forEach((t, i) => {
        const newOrder = i + 1;
        const groupChanged = t.id === dragged.id && sourceGroup !== destGroup;
        const orderChanged = t.order !== newOrder;
        if (groupChanged || orderChanged) {
            updates.push(
                equipmentTypeDao.updateEquipmentType(t.id, {
                    order: newOrder,
                    ...(groupChanged ? { group: destGroup } : {}),
                }),
            );
        }
    });

    if (sourceGroup !== destGroup) {
        const sourceList = orderBy(
            types.filter((t) => t.group === sourceGroup && t.id !== dragged.id && liveIds.has(t.id)),
            'order',
        );
        sourceList.forEach((t, i) => {
            const newOrder = i + 1;
            if (t.order !== newOrder) {
                updates.push(equipmentTypeDao.updateEquipmentType(t.id, { order: newOrder }));
            }
        });
    }

    try {
        await Promise.all(updates);
        const movedAcross = sourceGroup !== destGroup;
        toast.success(movedAcross ? `Moved "${dragged.title}" to ${destGroup}` : 'Reordered');
        log.info(`Reordered EquipmentType ${dragged.id} in Space ${spaceId}`, {
            from: { group: sourceGroup, order: dragged.order },
            to: { group: destGroup, index: insertIndex },
        });
    } catch (err) {
        log.error('Failed to reorder EquipmentTypes', { err, draggedId: dragged.id });
        toast.error('Failed to reorder');
    }
}
